/**
 * Concurrency + backpressure gate for a batch of schedule calls.
 *
 * WHAT THIS IS
 *   A small admission controller. Callers hand it a thunk that performs one
 *   `POST /v1/api/tasks/schedule`; it runs at most `concurrency` of them at a
 *   time, and when ANY call comes back with a `Retry-After` it pauses the whole
 *   batch until that instant. It owns no HTTP and no UI.
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   Today the client fans out schedule calls with an uncapped `Promise.all` over
 *   every card (CORE-PIPELINE-REFERENCE §10). One user's 340-file batch
 *   therefore arrived at professorx as 340 concurrent schedule requests, each of
 *   which is ~10 Mongo writes (object-log, workflow, 3 tasks, taskIds + logs
 *   push) before the optimistic-lock loop even starts, on a primary that was
 *   already stalled. There is no per-edge concurrency cap anywhere either
 *   (correction 11), so nothing upstream absorbed it.
 *
 *   Two rules follow, and this module is both:
 *     - cap the fan-out at 4-6 (plan §4.2; 4 matches the existing local-upload
 *       cap, correction 22);
 *     - make Retry-After a SHARED gate. Per-file backoff is useless here: every
 *       file in the batch is talking to the same overloaded professorx, so if
 *       one call is told to wait 30 s, the other 339 must wait too. The pause is
 *       held on the gate, not on the card.
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §4.2 ("Cap concurrent schedule calls at 4-6 with a
 *   queue; one **shared** Retry-After gate per batch, not per file"),
 *   corrections 11, 22. CORE-PIPELINE-REFERENCE.md §10 ("Schedule").
 *
 * WIRED 2026-09-05 behind `featureFlags.clientQueuedState`: one instance lives on
 * `scheduleCoordinator`, and every schedule call goes through `gate.run(...)`.
 * With the flag OFF the convert path still fans out uncapped.
 */

/** Plan §4.2, matching the existing local-upload cap of 4. */
export const DEFAULT_SCHEDULE_CONCURRENCY = 4;

/** Plan §4.2 upper bound. Anything higher is clamped, not rejected. */
export const MAX_SCHEDULE_CONCURRENCY = 6;

export interface BatchScheduleGateOptions {
    /** Slots. Clamped to [1, MAX_SCHEDULE_CONCURRENCY]. */
    concurrency?: number;
    /** Injectable clock; defaults to `Date.now`. */
    now?: () => number;
    /** Injectable timer, so tests can drive it; defaults to `setTimeout`. */
    setTimer?: (fn: () => void, ms: number) => any;
    clearTimer?: (handle: any) => void;
}

interface QueueEntry<T> {
    task: () => Promise<T>;
    resolve: (value: T) => void;
    reject: (reason?: unknown) => void;
}

export class BatchScheduleGate {
    readonly concurrency: number;

    private readonly now: () => number;
    private readonly setTimer: (fn: () => void, ms: number) => any;
    private readonly clearTimer: (handle: any) => void;

    private readonly queue: QueueEntry<any>[] = [];
    private running = 0;
    private pausedUntil: number | null = null;
    private timerHandle: any = null;

    constructor(options: BatchScheduleGateOptions = {}) {
        const requested = options.concurrency ?? DEFAULT_SCHEDULE_CONCURRENCY;
        this.concurrency = Math.min(
            Math.max(1, Math.floor(requested)),
            MAX_SCHEDULE_CONCURRENCY,
        );
        this.now = options.now ?? (() => Date.now());
        this.setTimer = options.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
        this.clearTimer = options.clearTimer ?? ((handle) => clearTimeout(handle));
    }

    /** Calls currently in flight. */
    get inFlight(): number {
        return this.running;
    }

    /** Calls admitted to the gate but not yet started. */
    get queued(): number {
        return this.queue.length;
    }

    /** The instant the shared pause lifts, or null when the gate is open. */
    get pausedUntilMs(): number | null {
        return this.pausedUntil;
    }

    get isPaused(): boolean {
        return this.pausedUntil !== null && this.now() < this.pausedUntil;
    }

    /**
     * Run `task` once a slot is free and the gate is not paused. Resolves or
     * rejects with whatever the task did; a rejection frees the slot exactly
     * like a success, so one failing card cannot wedge the batch.
     */
    run<T>(task: () => Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            this.queue.push({ task, resolve, reject } as QueueEntry<any>);
            this.pump();
        });
    }

    /**
     * Pause every NOT-YET-STARTED call until `instantMs`. Calls already in
     * flight are deliberately left alone: their schedule request may already
     * have created a workflow server-side, and aborting it is what produces
     * orphans. Extending an existing pause wins; shortening it never does.
     */
    pauseUntil(instantMs: number): void {
        if (this.pausedUntil !== null && instantMs <= this.pausedUntil) return;
        this.pausedUntil = instantMs;
        this.armTimer();
    }

    /** Convenience for a parsed `Retry-After`: pause the batch for `delayMs`. */
    pauseFor(delayMs: number): void {
        this.pauseUntil(this.now() + Math.max(0, delayMs));
    }

    /** Lift the pause immediately (a user-pressed "Retry all"). */
    resume(): void {
        this.pausedUntil = null;
        this.disarmTimer();
        this.pump();
    }

    private armTimer(): void {
        this.disarmTimer();
        if (this.pausedUntil === null) return;
        const wait = Math.max(0, this.pausedUntil - this.now());
        this.timerHandle = this.setTimer(() => {
            this.timerHandle = null;
            // The pause may have been extended while this timer was pending.
            if (this.isPaused) {
                this.armTimer();
                return;
            }
            this.pausedUntil = null;
            this.pump();
        }, wait);
    }

    private disarmTimer(): void {
        if (this.timerHandle !== null) {
            this.clearTimer(this.timerHandle);
            this.timerHandle = null;
        }
    }

    private pump(): void {
        if (this.isPaused) {
            this.armTimer();
            return;
        }
        if (this.pausedUntil !== null) {
            // The instant passed without the timer having fired yet.
            this.pausedUntil = null;
            this.disarmTimer();
        }

        while (this.running < this.concurrency && this.queue.length > 0) {
            const entry = this.queue.shift()!;
            this.running++;
            let started: Promise<any>;
            try {
                started = Promise.resolve(entry.task());
            } catch (error) {
                // A thunk that throws synchronously must still free its slot.
                started = Promise.reject(error);
            }
            started.then(
                value => { this.settle(entry, () => entry.resolve(value)); },
                error => { this.settle(entry, () => entry.reject(error)); },
            );
        }
    }

    private settle(_entry: QueueEntry<any>, deliver: () => void): void {
        this.running--;
        deliver();
        this.pump();
    }
}

export function createBatchScheduleGate(
    options?: BatchScheduleGateOptions,
): BatchScheduleGate {
    return new BatchScheduleGate(options);
}
